/**
 * DraggableMarkerLayer - Marcadores arrastaveis no mapa
 * Permite arrastar entregas para atualizar suas coordenadas
 */

import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { map } from '../../map/core/MapView';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

/**
 * Cria um elemento HTML para o marcador de entrega
 * @param {number} index - Indice da entrega (1-based)
 * @param {string} color - Cor do marcador
 * @returns {HTMLElement} Elemento do marcador
 */
const createMarkerElement = (index, color) => {
  const el = document.createElement('div');
  el.className = 'delivery-marker';
  el.style.cssText = `
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${color};
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    font-weight: 600;
    font-size: 12px;
    color: #fff;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    user-select: none;
  `;
  el.textContent = String(index);
  el.title = `Entrega ${index} - Arraste para reposicionar`;

  // Hover effect
  el.addEventListener('mouseenter', () => {
    el.style.transform = 'scale(1.15)';
    el.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
  });
  el.addEventListener('mouseleave', () => {
    el.style.transform = 'scale(1)';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
  });

  return el;
};

const DraggableMarkerLayer = ({
  deliveries = [],
  onDeliveryDrag,
  onDeliveryClick,
}) => {
  const tokens = usePremiumTokens();
  const markersRef = useRef(new Map());

  // Limpar marcadores ao desmontar
  useEffect(() => {
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current.clear();
    };
  }, []);

  // Handler para inicio do drag
  const handleDragStart = useCallback((marker, el) => {
    el.style.cursor = 'grabbing';
    el.style.transform = 'scale(1.2)';
    el.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
    el.style.zIndex = '1000';
  }, []);

  // Handler para fim do drag
  const handleDragEnd = useCallback((deliveryId, marker, el) => {
    el.style.cursor = 'grab';
    el.style.transform = 'scale(1)';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    el.style.zIndex = '';

    const lngLat = marker.getLngLat();
    onDeliveryDrag?.(deliveryId, {
      lat: lngLat.lat,
      lng: lngLat.lng,
    });
  }, [onDeliveryDrag]);

  // Atualizar marcadores quando deliveries mudam
  useEffect(() => {
    const currentIds = new Set(deliveries.map((d) => d.id));
    const existingIds = new Set(markersRef.current.keys());

    // Remover marcadores de deliveries que nao existem mais
    existingIds.forEach((id) => {
      if (!currentIds.has(id)) {
        const marker = markersRef.current.get(id);
        if (marker) {
          marker.remove();
          markersRef.current.delete(id);
        }
      }
    });

    // Adicionar ou atualizar marcadores
    deliveries.forEach((delivery, index) => {
      const existingMarker = markersRef.current.get(delivery.id);
      const color = delivery.routeColor || tokens?.accent?.primary || '#00d4aa';

      if (existingMarker) {
        // Atualizar posicao se mudou
        const currentPos = existingMarker.getLngLat();
        if (currentPos.lng !== delivery.lng || currentPos.lat !== delivery.lat) {
          existingMarker.setLngLat([delivery.lng, delivery.lat]);
        }
        // Atualizar conteudo do elemento
        const el = existingMarker.getElement();
        if (el) {
          el.textContent = String(index + 1);
          el.style.backgroundColor = color;
        }
      } else {
        // Criar novo marcador
        const el = createMarkerElement(index + 1, color);

        const marker = new maplibregl.Marker({
          element: el,
          draggable: true,
          anchor: 'center',
        })
          .setLngLat([delivery.lng, delivery.lat])
          .addTo(map);

        // Event listeners
        marker.on('dragstart', () => handleDragStart(marker, el));
        marker.on('dragend', () => handleDragEnd(delivery.id, marker, el));

        // Click handler
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          onDeliveryClick?.(delivery);
        });

        markersRef.current.set(delivery.id, marker);
      }
    });
  }, [deliveries, tokens, handleDragStart, handleDragEnd, onDeliveryClick]);

  // Atualizar posicoes dos marcadores existentes quando delivery muda (sem recriar)
  useEffect(() => {
    deliveries.forEach((delivery, index) => {
      const marker = markersRef.current.get(delivery.id);
      if (marker) {
        const el = marker.getElement();
        if (el) {
          el.textContent = String(index + 1);
          const color = delivery.routeColor || tokens?.accent?.primary || '#00d4aa';
          el.style.backgroundColor = color;
        }
      }
    });
  }, [deliveries, tokens]);

  return null;
};

export default DraggableMarkerLayer;
